/**
 * @fileoverview Landing page payments table with mock data.
 * Simplified version of PaymentsTable for showcasing the UI.
 * @module features/landing/components/LandingPaymentsTable
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/shared/components/ui'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/shared/utils/money'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

/**
 * Props for LandingPaymentsTable component
 */
interface LandingPaymentsTableProps {
  className?: string
}

/**
 * Mock payment data for the landing page table
 */
const MOCK_PAYMENTS = [
  {
    id: '1',
    counterparty: 'Pacific Ltd.',
    date: '01 Dec',
    title: 'Invoice FV2025/12/08 - December rent',
    amount: -8529.00,
    currency: 'PLN',
    badge: { label: 'Rent', variant: 'outline' as const },
    isComplete: true,
  },
  {
    id: '2',
    counterparty: 'Google Cloud EMEA Ltd.',
    date: '01 Dec',
    title: 'Invoice GCP-2025-12-001 - Cloud services November',
    amount: -3139.51,
    currency: 'PLN',
    badge: { label: 'Software subscriptions', variant: 'outline' as const },
    isComplete: true,
  },
  {
    id: '3',
    counterparty: 'PayPal',
    date: '01 Dec',
    title: 'Fees from 28-11-25 - Group: 16754',
    amount: -9.60,
    currency: 'PLN',
    badge: { label: 'Payment fees', variant: 'outline' as const },
    isComplete: false,
  },
]

/**
 * Landing page payments table component.
 * Displays a simplified payments table with mock data.
 *
 * @component
 */
export function LandingPaymentsTable({ className }: LandingPaymentsTableProps): ReactElement {
  const { t } = useTranslation('features-landing')
  const isMobile = useIsMobile()

  return (
    <div className={cn('max-w-full overflow-x-auto flex-1', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 px-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm mr-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={isMobile ? t('demo.paymentsTable.search') : t('demo.paymentsTable.searchPlaceholder')}
              className={cn('pl-9 cursor-default', isMobile ? 'w-[120px]' : 'w-[280px]')}
              readOnly
            />
          </div>
          <button
            type="button"
            className="px-3 h-9 rounded-md text-sm transition-colors bg-muted text-muted-foreground cursor-default"
          >
            {t('demo.paymentsTable.invoices')}
          </button>
        </div>
      </div>

      {/* Table */}
      <Table className="w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-4">
              <span className="sr-only">Status</span>
            </TableHead>
            {!isMobile && <TableHead className="w-10">#</TableHead>}
            <TableHead className={cn('border-l border-border/20', isMobile ? 'pl-3 text-xs' : 'pl-6')}>
              {t('demo.paymentsTable.counterpartyTitle')}
            </TableHead>
            <TableHead className={cn('pr-6 text-right', isMobile ? 'w-24 text-xs' : 'w-36')}>
              {t('demo.paymentsTable.amount')} ⇅
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {MOCK_PAYMENTS.map((payment, idx) => (
            <TableRow key={payment.id} className="h-20 align-middle">
              {/* Status indicator */}
              <TableCell className="w-4 p-0">
                <div
                  className={cn(
                    'ml-1 h-14 w-1 rounded-full transition-colors',
                    payment.isComplete ? 'bg-[var(--border-positive)]' : 'bg-transparent'
                  )}
                />
              </TableCell>

              {/* Row number */}
              {!isMobile && (
                <TableCell className="w-10 text-xs text-muted-foreground select-none">
                  {idx + 1}
                </TableCell>
              )}

              {/* Counterparty / Title */}
              <TableCell className={cn('text-xs text-muted-foreground border-l border-border/20', isMobile ? 'pl-3' : 'pl-6')}>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isMobile ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-foreground font-medium truncate">
                            {payment.counterparty}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {payment.title}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-foreground font-medium truncate">
                        {payment.counterparty}
                      </span>
                    )}
                    <Badge variant={payment.badge.variant} className="text-[11px]">
                      {payment.badge.label}
                    </Badge>
                  </div>
                  {!isMobile && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground/70 shrink-0">{payment.date}</span>
                      <span className="text-muted-foreground/50">|</span>
                      <span className="truncate">{payment.title}</span>
                    </div>
                  )}
                </div>
              </TableCell>

              {/* Amount */}
              <TableCell className={cn('text-muted-foreground font-semibold pr-6 text-right whitespace-nowrap', isMobile ? 'w-24 text-sm' : 'w-36')}>
                {formatMoney(payment.amount, payment.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
